import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { CellType } from './table-cell/index.tsx';
// eslint-disable-next-line jira/styled/no-styled-import-alias
import { CellContainer as CellContainerOld } from './table-cell/styled.tsx';

export type RowType = {
	cells: CellType[];
	columnKeys: string[];
	isLastRow: boolean;
};

export const TableRow = ({ columnKeys, cells, isLastRow }: RowType) => {
	const Cells = useMemo(
		() =>
			cells
				.filter((cell) => columnKeys.includes(cell.key))
				.map(({ key, content }) => (
					<TableCell isLastRow={isLastRow} key={`table-row-cell-${key}`}>
						{content}
					</TableCell>
				)),
		[columnKeys, cells, isLastRow],
	);

	return <TableRowContainer>{Cells}</TableRowContainer>;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableRowContainer = styled.tr({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 6}px`,
	position: 'relative',
	borderWidth: 'initial',
	borderStyle: 'none',
	borderColor: 'initial',
	width: '100%',

	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const CellContainerNew = styled.td<{ isLastRow: boolean }>({
	appearance: 'none',
	borderColor: 'initial',
	borderImage: 'initial',
	borderStyle: 'none',
	borderWidth: 'initial',
	boxSizing: 'border-box',
	display: 'table-cell',
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
	position: 'relative',
	verticalAlign: 'middle',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 4}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	borderBottom: ({ isLastRow }) => (isLastRow ? 'none' : `1px solid ${token('color.border')}`),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		paddingLeft: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-of-type': {
		paddingRight: token('space.100'),
	},
});

const TableCell = componentWithCondition(
	() => isVisualRefreshEnabled(),
	CellContainerNew,
	CellContainerOld,
);
